import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable, take } from 'rxjs';

import { IdentityPerson, IdentityUser, PagedResponse, UserCore, UserSecurityRole } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { AuthService } from '@reg/shared/auth/auth.service';
import { format } from 'date-fns';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { DateFormat } from '../enum';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get and update user information
 */
@Injectable()
export class UserService {
    public static get INCLUDE_USER_ACCOUNT(): string {
        return 'userAccount';
    }

    private cachedUserMap: { [apiRequest: string]: UserCore; } = {};

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private authService: AuthService,
    ) {
    }

    public clearCache(): void {
        this.cachedUserMap = {};
    }

    /**
     * Get user information from the core database
     */
    public getCoreUser(userId: string | number, siteId?: number, forceRefresh: boolean = false): Observable<UserCore> {
        const apiRequestUrl: string =
            `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')}/api/users/${userId}`;

        if (!forceRefresh && this.cachedUserMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedUserMap[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const userCore: UserCore = (new UserCore()).deserialize(response);
                this.cachedUserMap[apiRequestUrl] = userCore.clone();
                return userCore;
            }));
    }

    /**
     * Get user information from the core database by the given email
     */
    public getCoreUserByEmail(email: string): Observable<UserCore> {
        const apiRequestUrl: string = `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')
        }/api/users/email/${email}`;

        return this.http.get(apiRequestUrl, false, false)
            .pipe(map((response: IJsonApiResponse) => (new UserCore().deserialize(response))));
    }

    /**
     * Get user information from the core database by the given email
     */
    public getCheckCoreUserByEmail(email: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')
        }/api/users/check-email-exists/${email}`;

        return this.http.get<ICheckCoreUser>(apiRequestUrl, false, false, this.http.getHeadersForJson(false))
            .pipe(map((response: ICheckCoreUser) => response.exists));
    }

    /**
     * Get user information from the core database by the given username
     */
    public getCheckCoreUserByUsername(username: string): Observable<boolean> {
        const baseUrl: string = this.http.configService.getBaseConfig('flosportsApiBaseUrl');
        const apiRequestUrl: string =
            `${baseUrl}/api/users/check-username-exists/${username}`;

        return this.http.get<ICheckCoreUser>(apiRequestUrl, false, false, this.http.getHeadersForJson(false))
            .pipe(map((response: ICheckCoreUser) => response.exists));
    }

    /**
     * Login to core
     */
    public login(email: string, password: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')}/api/tokens`;

        const json: any = {};
        json.email = email;
        json.password = password;

        return this.http
            .post<IUserToken>(apiRequestUrl, JSON.stringify(json), true, false, this.http.getHeadersForJson(false))
            .pipe(
                map((response: IUserToken) => {
                    const jwtToken: string = response.token;
                    const refreshToken: string = response.refresh_token;

                    if (jwtToken) {
                        this.authService.setJwtCookies(jwtToken, refreshToken);
                        return true;
                    }

                    return false;
                }),
                catchError((error: HttpErrorResponse) => {
                    this.http.toastService.showError('Invalid email and/or password', null, 5000);
                    throw (error);
                }),
            );
    }

    /**
     * Create a new core user
     * @returns number The newly created user id
     */
    public createUser(username: string, email: string, password: string): Observable<number> {
        let apiRequestUrl: string = `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')}/api/users`;
        apiRequestUrl += `?send_identify=1&site_id=${this.http.configService.getSportConfig('siteId')}`;

        const json: any = {};
        json.username = username;
        json.email = email;
        json.plain_password = password;

        return this.http
            .post<ICreateUser>(apiRequestUrl, JSON.stringify(json), true, true, this.http.getHeadersForJson(false))
            .pipe(map((response: ICreateUser) => response.user.id));
    }

    /**
     * Send the forgot password email
     */
    public forgotPassword(email: string): Observable<boolean> {
        const baseUrl: string = this.http.configService.getBaseConfig('flosportsApiBaseUrl');
        const apiRequestUrl: string = `${baseUrl}/api/users/forgot-password`;
        const body: any = { email: email };

        return this.http.post(apiRequestUrl, body).pipe(map((response: IJsonApiResponse) => !!response));
    }

    /**
     * Save the user's birthday to the core database
     */
    public saveCoreUserFromIdentityPerson(userId: number, identityPerson: IdentityPerson): Observable<boolean> {
        const baseUrl: string = this.http.configService.getBaseConfig('flosportsApiBaseUrl');
        const apiRequestUrl: string = `${baseUrl}/api/users/${userId}`;

        const body: any = {};
        body.first_name = identityPerson.firstName;
        body.last_name = identityPerson.lastName;

        if (identityPerson.email) {
            body.email = identityPerson.email;
        }

        const gender: string = (identityPerson.gender || '').toLowerCase();
        if (gender === IdentityPerson.GENDER_MALE ||
            gender === IdentityPerson.GENDER_FEMALE) {
            body.gender = gender;
        }

        if (identityPerson.location.zipCode) {
            body.zip = identityPerson.location.zipCode;
        }

        if (identityPerson.location.country) {
            body.country = identityPerson.location.country;
        }

        body.birthday = format(identityPerson.dateOfBirth, DateFormat.YYYYMMDD);

        // If failed, do it silently. This is not a crucial process, more of an added benefit
        return this.http.patch(apiRequestUrl, JSON.stringify(body), true, false)
            .pipe(
                map((response: any) => response.success),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Get all products for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = [],
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl);
    }

    /**
     * Get users by a url. This is used for paging through users since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const users: UserSecurityRole[] = [];
                for (const user of json.data) {
                    users.push((new UserSecurityRole()).deserialize(user, response.included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    users,
                );
            }));
    }

    /**
     * Get a single user's data
     */
    public get(eventId: string, userId: string, includes: string[] = null): Observable<UserSecurityRole> {
        const apiRequestUrl: string = this.getApiUrl(eventId, userId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new UserSecurityRole().deserialize(response))));
    }

    /**
     * Add or update the user
     */
    public save(user: UserSecurityRole, includes: string[] = null): Observable<UserSecurityRole> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(user.eventId, user.id, includes);

        if (user.id) {
            // Update existing User
            response = this.http.patch(apiRequestUrl, user.serialize());
        } else {
            // Add a new User
            response = this.http.post(apiRequestUrl, user.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new UserSecurityRole()).deserialize(response)));
    }

    /**
     * Delete the specified user.
     */
    public delete(eventId: string, userSecurityRoleId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, userSecurityRoleId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * If a user does not have a person record in identity we need to fetch the user so we can at least display their email
     * or name from the core database
     *
     * @param {IdentityUser[]} identityUsers
     * @param {() => void} callback
     */
    public getUserCoreIfNoPersonData(identityUsers: IdentityUser[] = [], callback?: () => void): void {
        let processedCount: number = 0;

        identityUsers.forEach((identityUser: IdentityUser) => {
            if (identityUser &&
                identityUser.floId &&
                (!identityUser.person || !identityUser.person.id)) {
                // We don't have a person to display so we need to get what we can from the user user
                this.getCoreUser(identityUser.floId)
                    .pipe(take(1))
                    .subscribe((userCore: UserCore) => {
                        processedCount++;
                        identityUser.person = identityUser.person || new IdentityPerson();
                        identityUser.person.firstName = userCore.firstName;
                        identityUser.person.lastName = userCore.lastName;
                        identityUser.person.email = userCore.email;

                        if (processedCount >= identityUsers.length && callback) {
                            // All users have been processed
                            callback();
                        }
                    });
            } else {
                processedCount++;
            }

            if (processedCount >= identityUsers.length && callback) {
                // All users have been processed
                callback();
            }
        });
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        userId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/users/`;

        if (userId) {
            url += userId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface ICheckCoreUser {
    exists: boolean;
}

export interface IUserToken {
    token: string;
    refresh_token: string;
}

export interface ICreateUser {
    user: IUserCore
}
export interface IUserCore {
    id: number;
    username: string;
}
